<script setup>

import useVuelidate from "@vuelidate/core"
import {email, helpers, maxLength, minLength, numeric, required, requiredIf} from "@vuelidate/validators"
import {onMounted, reactive, watch} from "vue"
import {Vue3SlideUpDown} from "vue3-slide-up-down"

import InputCheckbox from "@shared/components/atoms/form/InputCheckbox.vue"
import InputText from "@shared/components/atoms/form/InputText.vue"
import {useAccountStore} from "@shared/stores/AccountStore"
import {useNotificationsStore} from "@shared/stores/NotificationsStore"

const accountStore = useAccountStore()
const notificationsStore = useNotificationsStore()

const state = reactive({
    isChangePasswordVisible: false,
    form: {
        email: accountStore.user?.email ?? '',
        mobile_number: accountStore.user?.mobile_number ?? '',
        password: '',
        new_password: '',
        confirm_password: ''
    }
})

watch(
    () => accountStore.user,
    (currentUser) => {
        state.form.email = currentUser?.email ?? ''
        state.form.mobile_number = currentUser?.mobile_number ?? ''
    },
    {deep: true}
)

const $v = useVuelidate({
    email: {
        email
    },
    mobile_number: {
        numeric,
        minLength: helpers.withMessage('Please enter a valid mobile number.', minLength(9)),
        maxLength: helpers.withMessage('Please enter a valid mobile number.', maxLength(10))
    },
    password: {
        required
    },
    new_password: {
        requiredIf: requiredIf(() => state.form.confirm_password !== ''),
        minLength: helpers.withMessage('Password must be at least 8 characters.', minLength(8)),
        pattern: helpers.withMessage('Password field must contain at least one alphabet character and one number.', (value) => {
            return /^(?=.*[A-Za-z])(?=.*\d).+$/.test(value)
        })
    },
    confirm_password: {
        requiredIf: requiredIf(() => state.form.new_password !== '')
    }
}, state.form)

const handleSubmit = () => {
    $v.value.$touch()
    if (!$v.value.$invalid) {
        accountStore.patchUserProfile(state.form)
            .then(() => {
                accountStore.closeProfileModal()
                notificationsStore.showAlert('Profile details successfully updated.')
            })
            .catch((error) => {
                notificationsStore.showAlert(error)
            })
    }
}

onMounted(function() {
    state.isChangePasswordVisible = false
})

</script>

<template>
    <div v-if="accountStore.isProfileModalOpen"
         id="divAppUserProfile"
         class="show"
    >
        <div class="divUserProfileOverlay"></div>
        <div class="divUserProfilePopup">
            <div id="btnCloseUserProfile" @click="accountStore.closeProfileModal"></div>
            <div id="divUserProfileHeading">
                {{ accountStore.user?.name }}
            </div>
            <div class="divUserProfileForm">

                <div id="txtUserProfileEmail">
                    <InputText
                        label="Email"
                        autocomplete="off"
                        type="email"
                        v-model="$v.email.$model"
                        :validator="$v.email"
                    />
                </div>

                <div id="txtUserProfileMobileNumber">
                    <InputText
                        label="Mobile number"
                        type="number"
                        v-model="$v.mobile_number.$model"
                        :validator="$v.mobile_number"
                    />
                </div>

                <div id="txtUserProfilePassword">
                    <InputText
                        label="Current password"
                        type="password"
                        v-model="$v.password.$model"
                        :validator="$v.password"
                    />
                </div>

                <InputCheckbox v-model="state.isChangePasswordVisible">
                   Change password?
                </InputCheckbox>

                <Vue3SlideUpDown v-model="state.isChangePasswordVisible">
                    <div id="txtUserProfileNewPassword">
                        <InputText
                            label="New password"
                            type="password"
                            v-model="$v.new_password.$model"
                            :validator="$v.new_password"
                        />
                    </div>

                    <div id="txtUserProfileConfirmPassword">
                        <InputText
                            label="Confirm password"
                            type="password"
                            v-model="$v.confirm_password.$model"
                            :validator="$v.confirm_password"
                        />
                    </div>
                </Vue3SlideUpDown>

                <button id="btnUpdateProfile" class="btnGreen" @click="handleSubmit">
                    UPDATE PROFILE
                </button>
            </div>
        </div>
    </div>
</template>
